/**
 * Browser-side test hook.
 * Installed by main.ts after the initial map loads. Exposes window.__fallline
 * so that Playwright scripts (and manual DevTools) can drive the game
 * deterministically.
 */

import type { Engine } from '../core/Engine';
import type { MouseLook } from '../core/MouseLook';
import type { BotResult, BotRoute } from '../game/bot';
import { createRouteRunner } from '../game/bot';
import type { RunTimer } from '../game/RunTimer';
import { EYE_HEIGHT } from '../movement/constants';
import type { PlayerController } from '../movement/PlayerController';
import type { BuiltLevel } from '../world/TestLevel';

// ---------------------------------------------------------------------------
// Window augmentation
// ---------------------------------------------------------------------------

export interface FallLineHook {
  ready: boolean;
  mapId: () => string;
  loadMap: (id: string) => Promise<void>;
  getState: () => {
    pos: [number, number, number];
    hspd: number;
    grounded: boolean;
    surfing: boolean;
    timer: {
      state: string;
      ticks: number;
      valid: boolean;
      splits: number[];
    };
  };
  runRoute: (
    route: BotRoute,
    opts?: { chunkTicks?: number; startYaw?: number; pauseAtTicks?: number[] },
  ) => Promise<BotResult>;
  /** Tick count at which the route is currently paused, or undefined if not paused. */
  pausedAt?: number;
  /** Resolve a mid-run pause so execution continues. */
  resume: () => void;
  setCamera: (pos: [number, number, number], yawDeg: number, pitchDeg: number) => void;
  measureFps: (ms: number) => Promise<number>;
  /** Activate the current hero's ability by id (for visual tests of spawned objects). */
  castAbility: (id: string) => void;
  /** Live combat state (player + declared dummies) for playtest asserts (spec 0026). */
  getCombatState: () => Array<{ id: string; health: number; alive: boolean }>;
  /** Select a hero by roster id (combat playtests need a hero with a kit). */
  selectHero: (id: string) => void;
  /** Teleport the player's feet (deterministic positioning for combat playtests). */
  teleportPlayer: (pos: [number, number, number]) => void;
}

declare global {
  interface Window {
    __fallline?: FallLineHook;
  }
}

// ---------------------------------------------------------------------------
// Deps interface
// ---------------------------------------------------------------------------

export interface PageHookDeps {
  switchMap: (id: string) => Promise<void>;
  getLevel: () => BuiltLevel;
  getPlayer: () => PlayerController;
  getTimer: () => RunTimer;
  engine: Engine;
  look: MouseLook;
  setBotActive: (b: boolean) => void;
  castAbility: (id: string) => void;
  getCombatState: () => Array<{ id: string; health: number; alive: boolean }>;
  selectHero: (id: string) => void;
  teleportPlayer: (pos: [number, number, number]) => void;
}

// ---------------------------------------------------------------------------
// Install
// ---------------------------------------------------------------------------

export function installPageHook(deps: PageHookDeps): void {
  const {
    switchMap,
    getLevel,
    getPlayer,
    getTimer,
    engine,
    look,
    setBotActive,
    castAbility,
    getCombatState,
    selectHero,
    teleportPlayer,
  } = deps;

  const hook: FallLineHook = {
    ready: true,

    pausedAt: undefined,

    // Default no-op; replaced during runRoute pauses.
    resume: () => {},

    mapId: () => getLevel().mapId,

    loadMap: async (id: string) => {
      await switchMap(id);
    },

    getState: () => {
      const player = getPlayer();
      const timer = getTimer();
      return {
        pos: [player.position.x, player.position.y, player.position.z],
        hspd: player.horizontalSpeed(),
        grounded: player.grounded,
        surfing: player.surfing,
        timer: {
          state: timer.state,
          ticks: timer.ticks,
          valid: timer.valid,
          splits: timer.splits.map((s) => s.ticks),
        },
      };
    },

    runRoute: async (
      route: BotRoute,
      opts: { chunkTicks?: number; startYaw?: number; pauseAtTicks?: number[] } = {},
    ): Promise<BotResult> => {
      const chunkTicks = opts.chunkTicks ?? 300;
      const startYaw = opts.startYaw ?? look.yaw;
      const pauseAtTicks = opts.pauseAtTicks ? [...opts.pauseAtTicks].sort((a, b) => a - b) : [];
      const player = getPlayer();
      const timer = getTimer();

      // Reset timer so the bot gets a fresh run.
      timer.reset();
      player.respawn();

      // Hide UI overlays so screenshots show only the 3-D view.
      const overlayEl = document.querySelector<HTMLElement>('#overlay');
      const bannerEl = document.querySelector<HTMLElement>('#run-banner');
      if (overlayEl) overlayEl.style.display = 'none';
      if (bannerEl) bannerEl.style.display = 'none';

      setBotActive(true);

      const runner = createRouteRunner(player, timer, route, startYaw);

      // Sorted pause-tick thresholds we still need to hit.
      const remainingPauses = [...pauseAtTicks];

      try {
        // Run in chunks, yielding to the event loop (via requestAnimationFrame)
        // between chunks so the engine can render frames for screenshots.
        let totalTicks = 0;
        while (true) {
          // Determine how many ticks to run before the next pause point.
          const nextPause = remainingPauses[0];
          const ticksUntilPause =
            nextPause !== undefined ? Math.max(1, nextPause - totalTicks) : chunkTicks;
          const thisChunk = Math.min(chunkTicks, ticksUntilPause);

          const done = runner.stepMany(thisChunk);
          totalTicks += thisChunk;

          // Snap camera to player eye position after each chunk.
          const currentYaw = runner.currentYaw();
          const p = player.position;
          engine.camera.position.set(p.x, p.y + EYE_HEIGHT, p.z);
          engine.camera.rotation.set(-0.15, currentYaw, 0, 'YXZ');
          look.yaw = currentYaw;

          if (done) break;

          // Check if we've reached a pause tick.
          if (nextPause !== undefined && totalTicks >= nextPause) {
            remainingPauses.shift();
            hook.pausedAt = totalTicks;
            // Yield to the render loop once to let the frame draw.
            await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
            // Wait for external resume() call.
            await new Promise<void>((resolve) => {
              hook.resume = () => {
                hook.pausedAt = undefined;
                resolve();
              };
            });
          }

          // Yield to the browser's render loop.
          await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        }
      } finally {
        setBotActive(false);
        // Restore UI overlays.
        if (overlayEl) overlayEl.style.display = '';
        if (bannerEl) bannerEl.style.display = '';
        hook.pausedAt = undefined;
      }

      return runner.result();
    },

    setCamera: (pos: [number, number, number], yawDeg: number, pitchDeg: number) => {
      engine.camera.position.set(pos[0], pos[1], pos[2]);
      const yaw = (yawDeg * Math.PI) / 180;
      const pitch = (pitchDeg * Math.PI) / 180;
      engine.camera.rotation.set(pitch, yaw, 0, 'YXZ');
      look.yaw = yaw;
      look.pitch = pitch;
    },

    castAbility,

    getCombatState,

    selectHero,

    teleportPlayer,

    measureFps: (ms: number): Promise<number> => {
      return new Promise<number>((resolve) => {
        let count = 0;
        const start = performance.now();
        const frame = () => {
          count++;
          if (performance.now() - start < ms) {
            requestAnimationFrame(frame);
          } else {
            resolve(count);
          }
        };
        requestAnimationFrame(frame);
      });
    },
  };

  window.__fallline = hook;
}
